import { Box, Paper, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { IIntegration } from '@/entities/integrations';
import { useProfile } from '@/entities/profile';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { CustomTypography } from '@/shared/ui/Typography';

import { useIntegrationsByAccountId } from '../../model/useIntegrationsByAccountId';
import { ConnectAmoCrmButton } from '../ConnectAmoCrmButton';
import { IntegrationsStatus } from '../IntegrationsStatus';

import { IntegrationActiveSwitcher } from './IntegrationActiveSwitcher';
import { IntegrationDeleteButton } from './IntegrationDeleteButton';

const CRM_NAMES: Record<string, string> = {
  amocrm: 'AmoCRM',
  bitrix24: 'Bitrix24',
  hubspot: 'HubSpot',
  pipedrive: 'Pipedrive',
  salesforce: 'Salesforce',
};

const IntegrationRow = ({
  integration,
  t,
}: {
  integration: IIntegration;
  t: (key: string) => string;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: '1px solid',
      borderColor: 'custom.grey6',
      borderRadius: '16px',
      px: { xs: 1.5, md: 2 },
      py: 1.25,
    }}
  >
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'stretch', md: 'center' }}
    >
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: { md: 220 } }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <CustomTypography sx={{ fontWeight: 600 }}>
            {CRM_NAMES[integration.provider_type ?? ''] ?? 'CRM'}
          </CustomTypography>
          <IntegrationsStatus status={integration.is_active ?? false} />
        </Stack>
        <CustomTypography sx={{ color: 'custom.black.40', fontSize: 14 }}>
          {integration.crm_domain}
        </CustomTypography>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ flex: 1.6 }}
      >
        <Box sx={{ flex: 1 }}>
          <CustomTypography variant="caption" sx={{ color: 'custom.black.40' }}>
            {t('integrations:listTable.company')}
          </CustomTypography>
          <CustomTypography sx={{ fontWeight: 500 }}>
            {integration.company_name ||
              integration.company_id ||
              t('integrations:listTable.allCompanies')}
          </CustomTypography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <CustomTypography variant="caption" sx={{ color: 'custom.black.40' }}>
            {t('integrations:listTable.account')}
          </CustomTypography>
          <CustomTypography sx={{ fontWeight: 500 }}>
            {integration.crm_account_id}
          </CustomTypography>
        </Box>
      </Stack>

      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}
      >
        {!integration.is_active && (
          <IntegrationDeleteButton integration={integration} />
        )}
        <IntegrationActiveSwitcher integration={integration} />
      </Box>
    </Stack>
  </Paper>
);

export const IntegrationsList = () => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const { data: integrations, isPending } = useIntegrationsByAccountId(
    profile?.account_id
  );

  if (isPending) {
    return <Loader containerSize="medium" />;
  }

  if (!integrations?.length) {
    return (
      <NotFoundData
        title={t('integrations:notFoundData.title')}
        caption={t('integrations:notFoundData.caption')}
        actionSlot={<ConnectAmoCrmButton />}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ConnectAmoCrmButton />
      </Box>
      <Stack spacing={1}>
        {integrations.map((integration) => (
          <IntegrationRow
            key={integration.integra_id}
            integration={integration}
            t={t}
          />
        ))}
      </Stack>
    </Stack>
  );
};
